import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[Palaestra] Uncaught error:', error, info.componentStack);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-bg flex items-center justify-center p-6">
                    <div className="glass-card p-8 max-w-sm w-full text-center space-y-5">
                        <div className="w-16 h-16 rounded-2xl bg-red-950/50 flex items-center justify-center mx-auto">
                            <AlertTriangle className="w-8 h-8 text-red-400" />
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-white mb-1">Something went wrong</h1>
                            <p className="text-sm text-zinc-500">
                                An unexpected error occurred. Your training data is safe.
                            </p>
                        </div>
                        {this.state.error && (
                            <pre className="text-xs text-red-400/70 bg-red-950/20 rounded-lg p-3 overflow-auto max-h-24 text-left">
                                {this.state.error.message}
                            </pre>
                        )}
                        <button
                            onClick={this.handleReset}
                            className="inline-flex items-center gap-2 bg-gradient-gold text-stone-950 font-semibold text-sm px-5 py-2.5 rounded-xl shadow-glow-sm hover:shadow-glow-md active:scale-95 transition-all"
                        >
                            <RotateCcw className="w-4 h-4" />
                            Restart App
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
